<template>
    <div>
        <div class="row">
            <div class="col-12 col-sm-4 col-md-12 col-xl-4">
                <base-input
                    :label="$t('eventRegistration.person.zip')"
                    :required="required"
                    :rules="{length: 5, numeric: true}"
                    :model-value="person.zipValue"
                    @update:model-value="person.zipValue = $event.replace(/\D/g,'').substring(0, 5)" />
            </div>
            <div class="col-12 col-sm-8 col-md-12 col-xl-8 ">
                <BaseSelect
                    :model-value="person.cityId"
                    :clearable="!required"
                    :disabled="!isZipValueValid"
                    :loading="loadingAddressSuggestions"
                    autocomplete="off"
                    :label="$t('eventRegistration.person.city')"
                    :required="required"
                    :rules="{addressIsIncomplete: isZipValueValid}"
                    :loading-text="$t('eventRegistration.person.loadCities')"
                    :no-data-text="$t('eventRegistration.person.noCitiesFound')"
                    :placeholder="citySelectPlaceholder"
                    @update:model-value="updateCity"
                    @visible-change="selectVisibilityChange">
                    <BaseOption
                        v-for="citySuggestion in citySuggestions"
                        :key="citySuggestion.cityId"
                        :label="citySuggestion.cityValue"
                        :value="citySuggestion.cityId" />
                </BaseSelect>
            </div>
        </div>
        <BaseCheckbox
            v-if="isPostboxAddress && isStreetAddress"
            v-model="isManualPostboxAddress"
            class="mb-3">
            {{ $t('eventRegistration.person.isPostbox') }}
        </BaseCheckbox>
        <base-input
            v-if="isPostboxAddress && (!isStreetAddress || isManualPostboxAddress)"
            v-model="person.postboxNumber"
            :label="$t('eventRegistration.person.postboxNumber')"
            :required="required"
            :rules="{isInvalid: !isPostNumberValid, addressIsIncomplete: isZipValueValid, max: 30}" />
        <div
            v-if="isStreetAddress && !isManualPostboxAddress"
            class="row">
            <div class="col-12 col-sm-8 col-md-12 col-xl-8 ">
                <BaseSelect
                    :model-value="person.streetId"
                    :label="$t('eventRegistration.person.street')"
                    :required="required"
                    :rules="{addressIsIncomplete: isZipValueValid}"
                    :clearable="!required"
                    :disabled="!isZipValueValid || !person.cityId"
                    :filterable="true"
                    :loading="loadingAddressSuggestions"
                    :loading-text="$t('eventRegistration.person.loadStreets')"
                    :no-data-text="$t('eventRegistration.person.noStreetsFound')"
                    :placeholder="streetSelectPlaceholder"
                    autocomplete="off"
                    data-is-search="true"
                    @update:model-value="updateStreet"
                    @visible-change="selectVisibilityChange">
                    <BaseOption
                        v-for="streetSuggestion in streetSuggestions"
                        :key="streetSuggestion.streetId"
                        :label="streetSuggestion.streetValue"
                        :value="streetSuggestion.streetId" />
                </BaseSelect>
            </div>
            <div class="col-12 col-sm-4 col-md-12 col-xl-4">
                <base-input
                    v-model="person.houseNumber"
                    :label="$t('eventRegistration.person.houseNumber')"
                    :required="required"
                    :rules="{isInvalid: !isHouseNumberValid, addressIsIncomplete: isZipValueValid, max: 10}" />
            </div>
        </div>
        <div v-if="showAdditionalAddressField">
            <base-input
                v-model="person.additional1"
                :label="$t('eventRegistration.person.addressAdditional')"
                :rules="{max: 80}" />
        </div>
    </div>
</template>

<script>
import formSelectChoiceMixin from "@/mixins/registration/formSelectChoiceMixin";
import registrationPersonMixin from "@/mixins/registration/registrationPersonMixin";
import detectMobileMixin from "@/mixins/detectMobileMixin";
import debounce from 'lodash/debounce'

export default {
    name: "RegistrationAddressSelect",
    mixins: [formSelectChoiceMixin, registrationPersonMixin, detectMobileMixin],
    props: {
        required: {
            type: Boolean,
            default: false
        },
        disableAddressWatchers: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            currentLoadedZip: "",
            addressSuggestions: null,
            loadingAddressSuggestions: false,
            loadingAddressCheck: false,
            isHouseNumberValid: true,
            cancelCheckValidCheck: null,
            isManualPostboxAddress: false
        }
    },
    computed: {
        citySuggestions() {
            if (!this.isZipValueValid) {
                return []
            } else if (this.addressSuggestions) {
                return this.addressSuggestions.map((addressSuggestion) => {
                    return {
                        cityId: addressSuggestion.city?.key,
                        cityValue: addressSuggestion.city?.name,
                        communityIdentificationNumber: addressSuggestion.city?.ags,
                        zipValue: addressSuggestion.zip
                    }
                })
            } else if (this.person && this.person.cityId && this.person.cityValue) {
                return [{
                    cityId: this.person.cityId,
                    cityValue: this.person.cityValue,
                    communityIdentificationNumber: this.person.communityIdentificationNumber,
                    zipValue: this.person.zipValue
                }]
            } else {
                return []
            }
        },
        citySelectPlaceholder() {
            if (!this.isZipValueValid) {
                return this.$t('eventRegistration.person.pleaseInputZipFirst')
            } else if (this.loadingAddressSuggestions) {
                return this.$t('eventRegistration.person.loadCities')
            } else if (this.citySuggestions.length) {
                return this.$t('eventRegistration.person.chooseCity')
            } else {
                return this.$t('eventRegistration.person.noCitiesFound')
            }
        },
        streetSuggestions() {
            if (!this.isZipValueValid || !this.person.cityId) {
                return []
            } else if (this.addressSuggestions) {
                if (this.currentZipArea) {
                    return this.currentZipArea.streets.map((street) => {
                        return {
                            streetId: street.key,
                            streetValue: street.name
                        }
                    })
                } else {
                    return []
                }
            } else if (this.person && this.person.streetId && this.person.streetValue) {
                return [{
                    streetId: this.person.streetId,
                    streetValue: this.person.streetValue
                }]
            } else {
                return []
            }
        },
        streetSelectPlaceholder() {
            if (!this.isZipValueValid) {
                return this.$t('eventRegistration.person.pleaseInputZipFirst')
            } else if (!this.person.cityId) {
                return this.$t('eventRegistration.person.pleaseInputCityFirst')
            } else if (this.loadingAddressSuggestions) {
                return this.$t('eventRegistration.person.loadStreets')
            } else if (this.streetSuggestions.length) {
                return this.$t('eventRegistration.person.chooseStreet')
            } else {
                return this.$t('eventRegistration.person.noStreetsFound')
            }
        },
        currentZipArea() {
            if (this.addressSuggestions) {
                return this.addressSuggestions.find((addressSuggestion) => addressSuggestion.zip === this.person.zipValue && addressSuggestion.city?.key === this.person.cityId) ?? null
            } else {
                return {}
            }
        },
        isZipValueValid() {
            return !!(this.person.zipValue && this.person.zipValue.length === 5 && !isNaN(this.person.zipValue));
        },
        isPostboxAddress() {
            return !!((this.currentZipArea && this.currentZipArea.postboxes && this.currentZipArea.postboxes.length ||
                this.person.isPostbox === true
            ))
        },
        isStreetAddress() {
            return !!((this.currentZipArea && this.currentZipArea.streets && this.currentZipArea.streets.length) ||
                this.person.isPostbox === false
            )
        },
        isPostNumberValid() {
            //Wenn keine addressSuggestions geladen sind, wurde die Adresse vom Server geladen.
            //Deswegen gehen wir davon aus, dass die Nummer korrekt ist.
            if (!this.addressSuggestions) {
                return true;
            }

            const postboxNumber = this.$get(this.person, 'postboxNumber', '')
            return !!(postboxNumber &&
                postboxNumber.length &&
                this.currentZipArea &&
                this.currentZipArea.postboxes &&
                this.currentZipArea.postboxes.find((postboxRange) => {
                    return postboxRange.numbers_from <= postboxNumber && postboxRange.numbers_to >= postboxNumber
                })
            )
        },
        showAdditionalAddressField() {
            return !!(this.person && this.person.cityId &&
                (this.isPostboxAddress || this.isManualPostboxAddress || this.person.streetId))
        }
    },
    methods: {
        checkForValidHouseNumber() {
            this.cancelCheckValidCheck && this.cancelCheckValidCheck();

            if (this.person.cityId && this.person.zipValue && this.person.streetId && this.person.houseNumber) {
                this.loadingAddressCheck = true
                this.checkForValidHouseNumberDebounced()
            }

        },
        checkForValidHouseNumberDebounced: debounce(async function () {
            this.cancelCheckValidCheck = this.$axios.CancelToken?.source();

            const result = await this.$axios.get(this.$apiRoute('api.registration.address.checkForValidHouseNumber'), {
                cancelToken: this.cancelCheckValidCheck?.token,
                params: {
                    cityId: this.person.cityId,
                    zip: this.person.zipValue,
                    streetId: this.person.streetId,
                    houseNumber: this.person.houseNumber
                }
            })

            this.isHouseNumberValid = result.data.isHouseNumberValid
            this.loadingAddressCheck = false

        }, 500),
        async loadAddressSuggestions($keepOldAddress = false) {
            if (!this.isZipValueValid) return

            //If currentLoadedZip is the same, skip new loading
            if (this.currentLoadedZip !== this.person.zipValue) {
                this.loadingAddressSuggestions = true
                const result = await this.$axios.get(this.$apiRoute('api.registration.address.index'), {
                    params: {
                        zipValue: this.person.zipValue
                    }
                })
                this.addressSuggestions = result.data.data
                this.currentLoadedZip = this.person.zipValue

                this.loadingAddressSuggestions = false
            }

            //If we have only one result, preselect this result if we do not want to keep the old results
            if (!$keepOldAddress && this.addressSuggestions.length === 1) {
                this.updateCity(this.addressSuggestions[0].city.key)
                // this.person.cityId = this.addressSuggestions[0]?.city.cityId ?? null
                // this.person.zipId = this.addressSuggestions[0]?.zipId ?? null
            }
        },
        selectVisibilityChange(opened) {
            if (opened) {
                if (!this.addressSuggestions) {
                    this.loadAddressSuggestions(true)
                }
                this.$nextTick(() => {
                    const input = document.querySelector('.el-select[data-is-search="true"]:hover .el-input__inner');
                    if (!input) return;
                    input.blur()
                    input.focus()
                });
            }
        },
        updateStreet(streetId) {
            const newPerson = this.$cloneDeep(this.person)
            newPerson.streetId = streetId ?? null
            if (streetId) {
                const selectedStreetSuggestions = this.streetSuggestions?.find((streetSuggestion) => streetSuggestion.streetId === streetId) ?? null
                if (selectedStreetSuggestions) newPerson.streetValue = selectedStreetSuggestions.streetValue ?? null
            }
            this.person = this.$cloneDeep(newPerson)
        },
        updateCity(cityId) {
            const newPerson = this.$cloneDeep(this.person)

            newPerson.cityId = cityId ?? null
            if (cityId) {
                const selectedCitySuggestion = this.citySuggestions?.find((citySuggestion) => citySuggestion.cityId === cityId) ?? null
                console.log(selectedCitySuggestion)
                if (selectedCitySuggestion) {
                    newPerson.zipValue = selectedCitySuggestion.zipValue ?? null
                    newPerson.cityValue = selectedCitySuggestion.cityValue ?? null
                    newPerson.communityIdentificationNumber = selectedCitySuggestion.communityIdentificationNumber ?? null
                }
            }
            this.person = this.$cloneDeep(newPerson)
        }
    },
    watch: {
        'person.zipValue': {
            handler(currentZipValue, oldZipValue) {
                if (!this.disableAddressWatchers) {
                    if ((oldZipValue && currentZipValue !== oldZipValue) || !this.isZipValueValid) {
                        this.person.cityId = null
                        this.person.streetId = null
                        this.person.additional1 = null
                        this.person.isPostbox = null
                        this.person.postboxNumber = null
                    }
                }

                this.loadAddressSuggestions()
            },
            immediate: true
        },
        'person.cityId': {
            handler(currentCityId, oldCityId) {
                if (!this.disableAddressWatchers) {
                    if (currentCityId !== oldCityId) {
                        this.person.streetId = null
                        this.person.isPostbox = null
                        this.person.postboxNumber = null

                        if (!currentCityId) {
                            this.person.zipValue = null
                            this.person.communityIdentificationNumber = null
                            this.person.cityValue = null
                        }
                    }
                }

                this.checkForValidHouseNumber()
            }
        },
        'person.streetId': {
            handler(currentStreetId) {
                if (!this.disableAddressWatchers && !currentStreetId) {
                    this.person.streetValue = null
                    this.person.houseNumber = null
                }
                this.checkForValidHouseNumber()
            }
        },
        streetSuggestions: {
            handler() {
                if (this.streetSuggestions && this.streetSuggestions.length && !this.person.streetId && this.person.streetValue) {
                    this.person.streetId = this.streetSuggestions.find((streetSuggestion) => streetSuggestion.streetValue === this.person.streetValue).streetId
                }
            }
        },
        'person.postboxNumber': {
            handler(currentPostboxNumber) {
                if (currentPostboxNumber) {
                    //Lade die Adress Vorschläge bei änderung der Postbox Nummer,
                    //um eine Validierung der Nummer anzustoßen.
                    if (!this.addressSuggestions) {
                        this.loadAddressSuggestions(true)
                    }
                    this.person.isPostbox = true
                }
            }
        },
        'person.houseNumber': {
            handler() {
                this.checkForValidHouseNumber()
            }
        },
        'isManualPostboxAddress': {
            handler(isManualPostboxAddress) {
                if (!this.disableAddressWatchers) {
                    if (isManualPostboxAddress) {
                        this.person.streetId = null
                        this.person.houseNumber = null
                    } else {
                        this.person.postboxNumber = null
                    }
                }
            }
        }
    }
}
</script>

<style scoped>

</style>
